/**
 * ICE servers for the WebRTC surfaces, fetched from the API rather than
 * hard-coded per component.
 *
 * Every client used to carry its own `stun:stun.l.google.com` literal and no
 * relay, so anyone whose network blocked UDP to the media server simply had no
 * path — while our own coturn box ran idle. The API hands back short-lived TURN
 * credentials alongside STUN, and passing the stream key is what earns the
 * relay (see ice.module.ts).
 *
 * Never let this fail a connection: if the lookup is slow or down, the caller
 * gets STUN and carries on exactly as before.
 */
const FALLBACK: RTCIceServer[] = [
  { urls: ['stun:turn.kardocloud.com:3478', 'stun:stun.l.google.com:19302'] },
];

let cached: { at: number; ttlMs: number; servers: RTCIceServer[]; key?: string } | null = null;

export async function getIceServers(streamKey?: string): Promise<RTCIceServer[]> {
  const now = Date.now();
  // Credentials expire, so the cache has to expire with them — and a different
  // stream key earns its own grant.
  if (cached && cached.key === streamKey && now - cached.at < cached.ttlMs) return cached.servers;

  try {
    const base = (import.meta.env.VITE_API_BASE ?? '/v1').replace(/\/$/, '');
    const url = `${base}/ice-servers${streamKey ? `?stream=${encodeURIComponent(streamKey)}` : ''}`;
    const r = await fetch(url, { headers: { accept: 'application/json' }, signal: AbortSignal.timeout(5000) });
    if (!r.ok) return FALLBACK;
    const data = await r.json();
    const servers: RTCIceServer[] = Array.isArray(data?.iceServers) && data.iceServers.length
      ? data.iceServers
      : FALLBACK;
    // Re-fetch a little before the credentials lapse rather than exactly on it.
    const ttlMs = Math.max(30_000, (Number(data?.ttl) || 600) * 1000 * 0.8);
    cached = { at: now, ttlMs, servers, key: streamKey };
    return servers;
  } catch {
    return FALLBACK;
  }
}
